import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import type { Job, VideoJobCreate } from "../lib/types";
import { cancelJob, deleteJob, shareJob, updateJob, submitVideoJob, deleteVideoJob } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useVideoJobs } from "../hooks/useVideoJobs";
import { useDataCache } from "../contexts/DataCacheContext";
import { ShareModal } from "./ShareModal";
import { VideoConfigPanel } from "./VideoConfigPanel";
import { VideoJobCard } from "./VideoJobCard";
import { Icon } from "./icons";
import {
  useAudioPeaks,
  peaksToBars,
  waveformBars,
  WAVEFORM_PX_PER_BAR,
  WAVEFORM_MIN_BARS,
  WAVEFORM_MAX_BARS,
} from "../lib/audioPeaks";
import { setAudioPeaks } from "../lib/api";

interface JobRowProps {
  job: Job;
  onRefresh: () => void;
  onDelete?: (jobId: string) => void;
  onCancel?: (jobId: string) => void;
  showUserId?: boolean;
  averageGenTime?: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelection?: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  queued: "Queued",
  processing: "Generating",
  complete: "Complete",
  failed: "Failed",
  cancelled: "Cancelled",
};

// Maps to .sample-meta.status-* CSS variants. The CSS only defines
// status-generating today; everything else falls back to the default.
function statusToken(status: Job["status"]): string {
  if (status === "processing" || status === "queued") return "generating";
  if (status === "failed") return "failed";
  if (status === "cancelled") return "cancelled";
  return "complete";
}

function modelLabel(model?: string): string | null {
  if (!model) return null;
  if (model === "qwen3-tts") return "Qwen3";
  if (model === "fish-s2-pro") return "Fish S2-Pro";
  if (model === "chatterbox") return "Chatterbox";
  return model;
}

function initials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins > 0 ? `${mins}:${secs.toString().padStart(2, "0")}` : `0:${secs.toString().padStart(2, "0")}`;
}

function timeAgo(dateStr: string): string {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const CLOUDWATCH_URL =
  "https://us-east-1.console.aws.amazon.com/cloudwatch/home?region=us-east-1#logsV2:log-groups/log-group/$252Faws$252Flambda$252Fchatter-webhook";

const SEG_COUNT = 20;

const HEADLINE_TAG_RE = /\[([a-z][a-z\s-]{0,20})\]/gi;

function renderHeadlineWithChips(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  HEADLINE_TAG_RE.lastIndex = 0;
  let key = 0;
  while ((m = HEADLINE_TAG_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      <span key={`chip-${key++}`} className="tag-chip readonly">
        {m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

interface CompleteTakeRowProps {
  jobId: string;
  takeIndex: number;
  // Pre-computed peaks (base64 uint8). When present, the waveform paints
  // synchronously without downloading the audio. Missing on legacy takes —
  // those decode once and lazy-backfill via POST /audio/peaks.
  peaks?: string;
  audioUrl?: string;
  duration?: number;
  durationLabel: string;
  showLabel: boolean;
  isThisPlaying: boolean;
  progress: number; // 0..1
  shareLoading: boolean;
  shareError: string | null;
  downloadName: string;
  onPlayPause: (takeIndex: number, e: React.MouseEvent) => void;
  onSeek: (takeIndex: number, fraction: number, e: React.MouseEvent) => void;
  onShare: (takeIndex: number, e: React.MouseEvent) => void;
  onTimeUpdate: (takeIndex: number, el: HTMLAudioElement) => void;
  onEnded: (takeIndex: number) => void;
  onLoadedMetadata: (takeIndex: number, el: HTMLAudioElement) => void;
  audioRef: (el: HTMLAudioElement | null) => void;
}

function CompleteTakeRow(props: CompleteTakeRowProps) {
  const {
    jobId,
    takeIndex,
    peaks: prePeaks,
    audioUrl,
    durationLabel,
    showLabel,
    isThisPlaying,
    progress,
    shareLoading,
    shareError,
    downloadName,
    onPlayPause,
    onSeek,
    onShare,
    onTimeUpdate,
    onEnded,
    onLoadedMetadata,
    audioRef,
  } = props;

  const waveRef = useRef<HTMLDivElement | null>(null);
  const localAudioRef = useRef<HTMLAudioElement | null>(null);
  const playedLayerRef = useRef<HTMLDivElement | null>(null);
  const [waveWidth, setWaveWidth] = useState(0);

  useEffect(() => {
    const el = waveRef.current;
    if (!el) return;
    setWaveWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setWaveWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // While playing, drive the played-overlay's clip-path directly via rAF so
  // the lit portion advances continuously instead of stepping forward each
  // time `timeupdate` fires (~4Hz). When paused, React's `progress` prop
  // takes over via the inline style below.
  useEffect(() => {
    if (!isThisPlaying) return;
    let frame = 0;
    const tick = () => {
      const audio = localAudioRef.current;
      const layer = playedLayerRef.current;
      if (audio && layer && audio.duration && Number.isFinite(audio.duration)) {
        const f = Math.max(0, Math.min(1, audio.currentTime / audio.duration));
        layer.style.clipPath = `inset(0 ${((1 - f) * 100).toFixed(3)}% 0 0)`;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isThisPlaying]);

  const barCount = Math.max(
    WAVEFORM_MIN_BARS,
    Math.min(WAVEFORM_MAX_BARS, Math.floor(waveWidth / WAVEFORM_PX_PER_BAR) || WAVEFORM_MIN_BARS)
  );
  // Backfill descriptor: if the take has no pre-computed peaks, useAudioPeaks
  // falls back to decoding the audio once and POSTs the result so next load
  // hits the fast path. Memoised so identity is stable across renders.
  const backfill = useMemo(
    () =>
      prePeaks
        ? undefined
        : {
            onBackfill: (encoded: string) => {
              void setAudioPeaks({ kind: "take", jobId, takeIndex }, encoded).catch(
                () => {
                  // Best-effort — backfill failures never surface to the UI.
                },
              );
            },
          },
    [prePeaks, jobId, takeIndex],
  );
  const peaks = useAudioPeaks(audioUrl, prePeaks, backfill);
  const bars = peaks ? peaksToBars(peaks, barCount) : waveformBars(takeIndex, barCount);

  // When playing, rAF owns the clip-path (passing `undefined` tells React not
  // to fight it). When paused/idle, snap to the React-tracked `progress`.
  const playedClipStyle: React.CSSProperties | undefined = isThisPlaying
    ? undefined
    : { clipPath: `inset(0 ${((1 - progress) * 100).toFixed(3)}% 0 0)` };

  return (
    <div className={`take ${isThisPlaying ? "playing" : ""}`}>
      <div className="take-label">{showLabel ? `Take ${takeIndex + 1}` : "Sample"}</div>
      <button
        type="button"
        className={`play-btn ${isThisPlaying ? "playing" : ""}`}
        onClick={(e) => onPlayPause(takeIndex, e)}
        title={isThisPlaying ? "Pause" : "Play"}
      >
        {isThisPlaying ? <Icon.Pause /> : <Icon.Play />}
      </button>
      <audio
        ref={(el) => {
          localAudioRef.current = el;
          audioRef(el);
        }}
        src={audioUrl}
        onTimeUpdate={(e) => onTimeUpdate(takeIndex, e.currentTarget)}
        onLoadedMetadata={(e) => onLoadedMetadata(takeIndex, e.currentTarget)}
        onEnded={() => onEnded(takeIndex)}
        // preload="none" so cold-rendering N sample cards doesn't trigger N
        // metadata GETs — the audio is only fetched once the user hits play
        // (or scrubs the waveform, which also calls play()).
        preload="none"
      />
      <div
        ref={waveRef}
        className="take-wave"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const fraction = (e.clientX - rect.left) / rect.width;
          onSeek(takeIndex, fraction, e);
        }}
        role="button"
        aria-label="Seek"
        title="Click to seek"
      >
        <div className="take-wave-layer base">
          {bars.map((h, i) => (
            <i key={i} style={{ height: `${h}px` }} />
          ))}
        </div>
        <div
          ref={playedLayerRef}
          className="take-wave-layer played"
          style={playedClipStyle}
        >
          {bars.map((h, i) => (
            <i key={i} style={{ height: `${h}px` }} />
          ))}
        </div>
      </div>
      <div className="take-time">{durationLabel}</div>
      <div className="take-icons take-icons-bright">
        {audioUrl && (
          <a
            href={audioUrl}
            download={downloadName}
            title="Download"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              display: "grid",
              placeItems: "center",
              color: "var(--ink-2)",
              textDecoration: "none",
            }}
          >
            <Icon.Download />
          </a>
        )}
        <button
          type="button"
          title={
            shareLoading
              ? "Creating share link…"
              : shareError
                ? shareError
                : "Share"
          }
          onClick={(e) => onShare(takeIndex, e)}
          disabled={shareLoading}
          style={{ color: "var(--ink-2)" }}
        >
          {shareLoading ? <Icon.Clock /> : shareError ? <Icon.X /> : <Icon.Link />}
        </button>
      </div>
    </div>
  );
}

// Smoothly chase a target number. When the target jumps (e.g., webhook
// reports 12% → 47%), animate over ~12ms per unit of change with ease-out
// so the seg-bar fills cell-by-cell instead of snapping. Initial mount
// reads the target as-is — no fake 0%-up sweep when loading a job that's
// already in flight.
function useAnimatedNumber(target: number, msPerUnit = 12): number {
  const [value, setValue] = useState(target);
  const valueRef = useRef(target);

  useEffect(() => {
    const start = valueRef.current;
    const delta = target - start;
    if (Math.abs(delta) < 0.5) {
      valueRef.current = target;
      setValue(target);
      return;
    }
    const startTime = performance.now();
    const duration = Math.min(1500, Math.max(150, Math.abs(delta) * msPerUnit));
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 2);
      const v = start + delta * eased;
      valueRef.current = v;
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, msPerUnit]);

  return value;
}

function ProcessingTakeRow({
  takeIndex,
  targetPct,
  elapsedFrom,
  onCancel,
  overlay = false,
}: {
  takeIndex: number;
  targetPct: number;
  elapsedFrom: string;
  onCancel: (e: React.MouseEvent) => void;
  overlay?: boolean;
}) {
  const animatedPct = useAnimatedNumber(targetPct);
  const pct = Math.max(0, Math.min(100, Math.round(animatedPct)));
  const filled = Math.round((animatedPct / 100) * SEG_COUNT);
  const cls = ["take", "take-generating", overlay ? "take-overlay" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls} aria-hidden={overlay ? "true" : undefined}>
      <div className="take-label take-label-active">Take {takeIndex + 1}</div>
      <div className="take-state-icon">
        <span className="spin-mini" />
      </div>
      <div className="take-progress">
        <div className="take-progress-row">
          <span className="take-progress-lbl">Generating</span>
          <span className="take-progress-pct">{pct}%</span>
        </div>
        <div className="seg-bar">
          {Array.from({ length: SEG_COUNT }, (_, i) => (
            <span
              key={i}
              className={`seg-cell ${i < filled ? "on" : ""} ${i === filled && filled < SEG_COUNT ? "edge" : ""}`}
            />
          ))}
        </div>
      </div>
      <div className="take-time take-time-dim">
        <ElapsedSeconds from={elapsedFrom} />
      </div>
      <div className="take-icons take-icons-dim">
        <button type="button" title="Cancel" onClick={onCancel} disabled={overlay}>
          <Icon.X />
        </button>
      </div>
    </div>
  );
}

function ElapsedSeconds({ from }: { from: string }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = new Date(from).getTime();
    setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    const t = setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [from]);
  return <>{elapsed}s</>;
}

function AdminLogModal({ job, onClose }: { job: Job; onClose: () => void }) {
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3 className="modal-title">Sample details</h3>
            <p className="modal-sub">Logs and metadata for this job.</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <Icon.X />
          </button>
        </div>

        {job.error && (
          <div className="err-banner">
            <strong style={{ display: "block", marginBottom: 4 }}>Error</strong>
            <span>{job.error}</span>
          </div>
        )}

        {job.logs && (
          <div>
            <div className="label" style={{ marginBottom: 6 }}>Logs</div>
            <pre
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 11,
                lineHeight: 1.5,
                color: "var(--ink-2)",
                background: "oklch(0.14 0.012 265)",
                border: "1px solid var(--hair)",
                borderRadius: 8,
                padding: 10,
                margin: 0,
                whiteSpace: "pre-wrap",
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {job.logs}
            </pre>
          </div>
        )}

        <div>
          <div className="label" style={{ marginBottom: 6 }}>Metadata</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: 12,
              rowGap: 4,
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 11,
              color: "var(--ink-2)",
            }}
          >
            <span style={{ color: "var(--ink-4)" }}>jobId</span>
            <span style={{ wordBreak: "break-all" }}>{job.jobId}</span>
            <span style={{ color: "var(--ink-4)" }}>userId</span>
            <span style={{ wordBreak: "break-all" }}>{job.userId}</span>
            <span style={{ color: "var(--ink-4)" }}>status</span>
            <span>{job.status}</span>
            <span style={{ color: "var(--ink-4)" }}>type</span>
            <span>{job.type}</span>
            {job.model && (
              <>
                <span style={{ color: "var(--ink-4)" }}>model</span>
                <span>{job.model}</span>
              </>
            )}
            <span style={{ color: "var(--ink-4)" }}>createdAt</span>
            <span>{new Date(job.createdAt).toLocaleString()}</span>
            <span style={{ color: "var(--ink-4)" }}>referenceKey</span>
            <span style={{ wordBreak: "break-all" }}>{job.referenceKey}</span>
            <span style={{ color: "var(--ink-4)" }}>referenceName</span>
            <span>{job.referenceName}</span>
            {job.gpuType && (
              <>
                <span style={{ color: "var(--ink-4)" }}>gpuType</span>
                <span>{job.gpuType}</span>
              </>
            )}
            {job.inferenceSeconds != null && (
              <>
                <span style={{ color: "var(--ink-4)" }}>inference</span>
                <span>{job.inferenceSeconds.toFixed(2)}s</span>
              </>
            )}
            {job.totalJobCost != null && (
              <>
                <span style={{ color: "var(--ink-4)" }}>cost</span>
                <span style={{ color: "var(--green)" }}>${job.totalJobCost.toFixed(4)}</span>
              </>
            )}
          </div>
        </div>

        <a
          href={CLOUDWATCH_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 11,
            color: "var(--accent)",
            textDecoration: "none",
          }}
        >
          Open CloudWatch Logs ↗
        </a>
      </div>
    </div>
  );
}

export function JobRow({
  job,
  onRefresh,
  onDelete,
  onCancel,
  showUserId,
  collapsed,
  onToggleCollapse,
  selectionMode,
  selected,
  onToggleSelection,
}: JobRowProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { references } = useDataCache();
  const isAdmin = user?.role === "super_admin";

  const [playingTakeIndex, setPlayingTakeIndex] = useState<number | null>(null);
  // Per-take 0..1 playhead fraction. Persists across pause/resume so the lit
  // portion of the waveform stays put when the user pauses; resets to 0 only
  // when the take ends naturally (or is re-seeked back to the start).
  const [takeProgress, setTakeProgress] = useState<Record<number, number>>({});
  const [showLogModal, setShowLogModal] = useState(false);
  const [shareLoadingFor, setShareLoadingFor] = useState<number | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState(job.displayName || job.referenceName);
  const [savingName, setSavingName] = useState(false);
  const editNameRef = useRef<HTMLInputElement>(null);
  const audioRefs = useRef<Map<number, HTMLAudioElement | null>>(new Map());
  // Seek requested before the audio's metadata had loaded — stored as 0..1
  // and applied once duration becomes known via loadedmetadata. Without this,
  // a click on the waveform pre-load would drop the seek and the take would
  // start playing from 0.
  const pendingSeek = useRef<Map<number, number>>(new Map());

  // Per-take "finishing" phase: when a take flips from processing→complete we
  // animate the seg-bar to 100, hold briefly, then crossfade the bar overlay
  // out to reveal the waveform underneath. Without this the row would snap
  // straight to the waveform mid-progress, which feels abrupt.
  const [finishing, setFinishing] = useState<Record<number, "anim" | "hold" | "fade">>({});
  const prevTakeStatusRef = useRef<Record<number, string>>({});
  const finishScheduledRef = useRef<Set<number>>(new Set());
  const finishTimersRef = useRef<number[]>([]);

  const isActive = job.status === "queued" || job.status === "processing";
  const isExpanded = !collapsed;
  const isComplete = job.status === "complete";
  // Video generation only needs ONE finished take. A job where a take got
  // stuck and was cancelled reads as "cancelled"/"failed" overall but can
  // still hold takes worth animating, so gate the video UI on the takes
  // rather than on the job's status.
  const hasUsableTake = (job.takes ?? []).some(
    (t) => t.status === "complete" && t.outputKey,
  );
  const { videos, isAnyActive: isVideoActive, addVideoJob, removeVideoJob } =
    // Same rule as the video section below: a job with one good take can have
    // videos, even if the job itself ended up cancelled or failed.
    useVideoJobs(isExpanded && hasUsableTake ? job.jobId : null);

  const [showVideoPanel, setShowVideoPanel] = useState(false);
  const [videoPanelInitial, setVideoPanelInitial] = useState<VideoJobCreate | undefined>(undefined);

  const voiceDisplayName = job.displayName || job.referenceName;
  const matchedRef = references.find(
    (r) => r.s3Key === job.referenceKey || r.name === job.referenceName
  );
  const voiceImageUrl = job.displayImageUrl || matchedRef?.imageUrl;
  const takes = job.takes ?? [];
  const takeCount = job.takeCount ?? takes.length;

  // Detect processing→complete transitions SYNCHRONOUSLY during render so
  // the very first frame that sees status="complete" already paints as
  // ProcessingTakeRow at target=100 — without this, React would render
  // CompleteTakeRow once before the effect set phase=anim, causing a
  // one-frame waveform flicker. Phases: anim (animate to 100) → hold
  // (linger at 100) → fade (overlay crossfades out, revealing the
  // CompleteTakeRow rendered behind it).
  const synthesizedPhase: Record<number, "anim"> = {};
  for (const t of takes) {
    const prev = prevTakeStatusRef.current[t.takeIndex];
    if (
      prev === "processing" &&
      t.status === "complete" &&
      !finishing[t.takeIndex] &&
      !finishScheduledRef.current.has(t.takeIndex)
    ) {
      synthesizedPhase[t.takeIndex] = "anim";
    }
  }

  useEffect(() => {
    takes.forEach((t) => {
      const prev = prevTakeStatusRef.current[t.takeIndex];
      prevTakeStatusRef.current[t.takeIndex] = t.status;
      if (
        prev === "processing" &&
        t.status === "complete" &&
        !finishScheduledRef.current.has(t.takeIndex)
      ) {
        const idx = t.takeIndex;
        finishScheduledRef.current.add(idx);
        setFinishing((f) => ({ ...f, [idx]: "anim" }));
        finishTimersRef.current.push(
          window.setTimeout(
            () => setFinishing((f) => ({ ...f, [idx]: "hold" })),
            720
          ),
          window.setTimeout(
            () => setFinishing((f) => ({ ...f, [idx]: "fade" })),
            970
          ),
          window.setTimeout(() => {
            setFinishing((f) => {
              const nf = { ...f };
              delete nf[idx];
              return nf;
            });
            finishScheduledRef.current.delete(idx);
          }, 1390)
        );
      }
    });
  }, [takes]);

  useEffect(() => {
    return () => {
      finishTimersRef.current.forEach(clearTimeout);
      finishTimersRef.current = [];
    };
  }, []);
  const justCompleted = job._prevStatus === "processing" && job.status === "complete";
  const cardAnimClass = job._exiting
    ? "card-exit"
    : justCompleted
      ? "card-pulse"
      : "card-enter";

  const handleVideoSubmit = async (data: VideoJobCreate) => {
    const newJob = await submitVideoJob(job.jobId, data);
    addVideoJob(newJob);
    setShowVideoPanel(false);
    setVideoPanelInitial(undefined);
  };

  const handleVideoDelete = async (videoJobId: string) => {
    try {
      await deleteVideoJob(job.jobId, videoJobId);
      removeVideoJob(videoJobId);
    } catch (err) {
      console.error("Failed to delete video job:", err);
    }
  };

  const handleVideoReload = (config: VideoJobCreate) => {
    setVideoPanelInitial(config);
    setShowVideoPanel(true);
  };

  const handlePlayTake = (takeIndex: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const target = audioRefs.current.get(takeIndex);
    if (!target) return;
    const syncProgress = (idx: number, el: HTMLAudioElement) => {
      if (el.duration && Number.isFinite(el.duration)) {
        setTakeProgress((prev) => ({
          ...prev,
          [idx]: Math.min(1, el.currentTime / el.duration),
        }));
      }
    };
    if (playingTakeIndex === takeIndex) {
      // Capture the exact paused position so the React-driven clip-path on
      // the next render matches what the rAF loop just rendered.
      syncProgress(takeIndex, target);
      target.pause();
      setPlayingTakeIndex(null);
      return;
    }
    if (playingTakeIndex !== null) {
      const prevEl = audioRefs.current.get(playingTakeIndex);
      if (prevEl) {
        syncProgress(playingTakeIndex, prevEl);
        prevEl.pause();
      }
    }
    void target.play();
    setPlayingTakeIndex(takeIndex);
  };

  const handleAudioTimeUpdate = (takeIndex: number, el: HTMLAudioElement) => {
    if (el.duration && Number.isFinite(el.duration)) {
      setTakeProgress((prev) => ({
        ...prev,
        [takeIndex]: Math.min(1, el.currentTime / el.duration),
      }));
    }
  };

  const handleSeek = (takeIndex: number, fraction: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const target = audioRefs.current.get(takeIndex);
    if (!target) return;
    const clamped = Math.max(0, Math.min(1, fraction));
    if (target.duration && Number.isFinite(target.duration)) {
      target.currentTime = clamped * target.duration;
      pendingSeek.current.delete(takeIndex);
    } else {
      // Audio metadata not loaded yet — stash and apply on loadedmetadata.
      pendingSeek.current.set(takeIndex, clamped);
    }
    setTakeProgress((prev) => ({ ...prev, [takeIndex]: clamped }));
    if (playingTakeIndex !== takeIndex) {
      if (playingTakeIndex !== null) {
        audioRefs.current.get(playingTakeIndex)?.pause();
      }
      void target.play();
      setPlayingTakeIndex(takeIndex);
    }
  };

  const handleLoadedMetadata = (takeIndex: number, el: HTMLAudioElement) => {
    const pending = pendingSeek.current.get(takeIndex);
    if (pending != null && el.duration && Number.isFinite(el.duration)) {
      el.currentTime = pending * el.duration;
      pendingSeek.current.delete(takeIndex);
    }
  };

  const handleShareTake = async (takeIndex: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (shareLoadingFor !== null) return;
    setShareLoadingFor(takeIndex);
    try {
      const result = await shareJob(job.jobId, takeIndex);
      setShareUrl(result.shareUrl);
    } catch (err) {
      console.error("Share failed:", err);
      setShareError("Failed to create share link.");
      setTimeout(() => setShareError(null), 4000);
    } finally {
      setShareLoadingFor(null);
    }
  };

  const handleCancel = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (onCancel) {
      onCancel(job.jobId);
      return;
    }
    try {
      await cancelJob(job.jobId);
      onRefresh();
    } catch (err) {
      console.error("Cancel failed:", err);
    }
  };

  const handleDelete = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    if (onDelete) {
      onDelete(job.jobId);
      return;
    }
    try {
      await deleteJob(job.jobId);
      onRefresh();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleRetry = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (job.type === "voice-convert") {
      navigate("/convert", {
        state: {
          referenceName: job.referenceName,
          referenceKey: job.referenceKey,
          diffusionSteps: job.diffusionSteps,
          similarityCfgRate: job.similarityCfgRate,
          intelligibilityCfgRate: job.intelligibilityCfgRate,
          lengthAdjust: job.lengthAdjust,
          sourceAudioUrl: job.sourceAudioUrl,
          sourceKey: job.sourceKey,
        },
      });
    } else {
      navigate("/generate", {
        state: {
          text: job.text,
          referenceName: job.referenceName,
          referenceKey: job.referenceKey,
          model: job.model,
          exaggeration: job.exaggeration,
          cfgWeight: job.cfgWeight,
          temperature: job.temperature,
          repetitionPenalty: job.repetitionPenalty,
          topP: job.topP,
          minP: job.minP,
          trimStart: job.trimStart,
          trimEnd: job.trimEnd,
          language: job.language,
        },
      });
    }
  };

  const handleSaveDisplayName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed === voiceDisplayName) {
      setEditingName(false);
      setEditNameValue(voiceDisplayName);
      return;
    }
    setSavingName(true);
    try {
      const newDisplayName = trimmed === job.referenceName ? "" : trimmed;
      await updateJob(job.jobId, { displayName: newDisplayName });
      setEditingName(false);
      onRefresh();
    } catch (err) {
      console.error("Failed to update display name:", err);
    } finally {
      setSavingName(false);
    }
  };

  const handleHeaderClick = () => {
    if (selectionMode) {
      onToggleSelection?.();
    } else {
      onToggleCollapse?.();
    }
  };

  const headlineText =
    job.text?.trim() ||
    (job.type === "voice-convert" ? "Voice conversion" : "—");

  const headlineNodes = renderHeadlineWithChips(headlineText);

  const shellClass = [
    "sample-card",
    isExpanded ? "open" : "",
    cardAnimClass,
    selected ? "is-selected" : "",
    job.status === "queued" ? "is-queued" : "",
    job.status === "processing" ? "is-processing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const selectedRingStyle = selected
    ? { boxShadow: "0 0 0 2px var(--accent) inset", background: "oklch(0.19 0.018 265)" }
    : undefined;

  // ── Header (shared by collapsed + expanded) ───────────────────────────
  const header = (
    <div className="sample-h" onClick={handleHeaderClick}>
      {selectionMode && (
        <input
          type="checkbox"
          checked={selected || false}
          onChange={() => onToggleSelection?.()}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 16,
            height: 16,
            accentColor: "var(--accent)",
            marginTop: 4,
            flexShrink: 0,
          }}
        />
      )}
      <div
        className="vc-avatar"
        aria-hidden="true"
        style={{
          width: 72,
          height: 72,
          borderRadius: 14,
          fontSize: 22,
          ...(voiceImageUrl ? { overflow: "hidden", padding: 0 } : null),
        }}
      >
        {voiceImageUrl ? (
          <img
            src={voiceImageUrl}
            alt=""
            crossOrigin="anonymous"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          initials(voiceDisplayName)
        )}
      </div>
      <div className="sample-q">
        <div
          className="lbl"
          style={{
            color: "var(--ink-2)",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <span>{voiceDisplayName}</span>
          {modelLabel(job.model) && (
            <>
              <span style={{ color: "var(--ink-4)" }}>·</span>
              <span>{modelLabel(job.model)}</span>
            </>
          )}
        </div>
        <div
          onClick={(e) => {
            if (isExpanded) e.stopPropagation();
          }}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: isExpanded ? 6 : 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
            cursor: isExpanded ? "text" : "pointer",
            userSelect: "text",
          }}
        >
          {headlineNodes}
        </div>
      </div>
      <div className={`sample-meta status-${statusToken(job.status)}`}>
        <span className="dot">●</span>
        <span>{STATUS_LABELS[job.status]}</span>
        <span>·</span>
        <span>{timeAgo(job.createdAt)}</span>
        {takeCount > 0 && (
          <>
            <span>·</span>
            <span>
              {takeCount} take{takeCount === 1 ? "" : "s"}
            </span>
          </>
        )}
        <span className="sample-chev" aria-hidden="true">
          <Icon.Caret />
        </span>
      </div>
    </div>
  );

  if (collapsed) {
    return (
      <div className={shellClass} style={selectedRingStyle}>
        {header}
      </div>
    );
  }

  // ── Expanded body ─────────────────────────────────────────────────────
  return (
    <div
      className={`${shellClass} ${isVideoActive ? "card-processing" : ""}`}
      style={selectedRingStyle}
    >
      {header}

      {showUserId && (
        <div
          style={{
            marginTop: 12,
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 10.5,
            color: "var(--amber)",
            letterSpacing: "0.04em",
          }}
        >
          USER · {job.userId.slice(0, 12)}…
        </div>
      )}

      {/* Voice meta + edit-name */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 14,
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 10.5,
          color: "var(--ink-3)",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        <span style={{ color: "var(--ink-4)" }}>Voice</span>
        {editingName ? (
          <input
            ref={editNameRef}
            type="text"
            value={editNameValue}
            onChange={(e) => setEditNameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveDisplayName();
              if (e.key === "Escape") {
                setEditingName(false);
                setEditNameValue(voiceDisplayName);
              }
            }}
            onBlur={handleSaveDisplayName}
            disabled={savingName}
            autoFocus
            style={{
              flex: 1,
              minWidth: 0,
              padding: "4px 8px",
              borderRadius: 6,
              background: "oklch(0.16 0.012 265)",
              border: "1px solid var(--hair-2)",
              color: "var(--ink)",
              fontFamily: "inherit",
              fontSize: 11,
              outline: "none",
            }}
          />
        ) : (
          <>
            <span style={{ color: "var(--ink)", textTransform: "none", letterSpacing: "0", fontFamily: "system-ui, sans-serif", fontSize: 13, fontWeight: 500 }}>
              {voiceDisplayName}
            </span>
            {isComplete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditNameValue(voiceDisplayName);
                  setEditingName(true);
                }}
                title="Edit voice name for this sample"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  display: "grid",
                  placeItems: "center",
                  color: "var(--ink-4)",
                  background: "transparent",
                  border: "1px solid transparent",
                }}
              >
                <Icon.Wand />
              </button>
            )}
          </>
        )}
      </div>

      {/* Failed-job error banner */}
      {job.status === "failed" && job.error && (
        <div className="err-banner" style={{ marginTop: 14 }}>
          {job.error}
        </div>
      )}

      {/* Failed-job logs */}
      {job.status === "failed" && job.logs && (
        <details style={{ marginTop: 8 }}>
          <summary
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 10.5,
              color: "var(--ink-3)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Show logs
          </summary>
          <pre
            style={{
              marginTop: 6,
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 11,
              lineHeight: 1.5,
              color: "var(--ink-2)",
              background: "oklch(0.14 0.012 265)",
              border: "1px solid var(--hair)",
              borderRadius: 8,
              padding: 10,
              whiteSpace: "pre-wrap",
              maxHeight: 160,
              overflowY: "auto",
            }}
          >
            {job.logs}
          </pre>
        </details>
      )}

      {/* Retry CTA for failed/cancelled jobs */}
      {(job.status === "failed" || job.status === "cancelled") && (
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button type="button" className="btn btn-line" onClick={handleRetry}>
            <Icon.Refresh /> Try again with same settings
          </button>
          {job.status === "failed" && isAdmin && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setShowLogModal(true)}
            >
              <Icon.Doc /> View logs
            </button>
          )}
        </div>
      )}

      {/* Takes list */}
      {takes.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
          {takes.map((take) => {
            const showLabel = takes.length > 1;
            const isThisPlaying = playingTakeIndex === take.takeIndex;
            const cancelledByParent =
              job.status === "cancelled" && take.status !== "failed" && take.status !== "complete";
            // Prefer the committed phase from state; fall back to the
            // synchronously-detected one so the first render of a status flip
            // already shows the bar instead of momentarily flashing the wave.
            const phase = finishing[take.takeIndex] ?? synthesizedPhase[take.takeIndex];

            // Queued / cancelled-by-parent
            if (take.status === "queued" || cancelledByParent) {
              return (
                <div key={take.takeIndex} className="take take-queued">
                  <div className="take-label">Take {take.takeIndex + 1}</div>
                  <div className="take-state-icon">
                    <Icon.Clock />
                  </div>
                  <div className="take-state-msg">
                    <span className="take-state-lbl">
                      {cancelledByParent ? "Cancelled" : "Queued"}
                    </span>
                  </div>
                  <div className="take-time take-time-dim">--:--</div>
                  <div className="take-icons take-icons-dim">
                    <button
                      type="button"
                      title="Cancel"
                      onClick={(e) => handleCancel(e)}
                      disabled={!isActive}
                    >
                      <Icon.X />
                    </button>
                  </div>
                </div>
              );
            }

            // Processing — or finishing while still animating to 100/holding.
            if (take.status === "processing" || phase === "anim" || phase === "hold") {
              const targetPct = phase
                ? 100
                : Math.max(0, Math.min(100, take.progress ?? 0));
              return (
                <ProcessingTakeRow
                  key={take.takeIndex}
                  takeIndex={take.takeIndex}
                  targetPct={targetPct}
                  elapsedFrom={job.startedAt || job.createdAt}
                  onCancel={(e) => handleCancel(e)}
                />
              );
            }

            // Failed take
            if (take.status === "failed") {
              return (
                <div
                  key={take.takeIndex}
                  className="take take-queued"
                  style={{ borderColor: "oklch(0.65 0.20 25 / 0.4)" }}
                >
                  <div className="take-label" style={{ color: "var(--red)" }}>
                    Take {take.takeIndex + 1}
                  </div>
                  <div className="take-state-icon" style={{ color: "var(--red)" }}>
                    <Icon.X />
                  </div>
                  <div className="take-state-msg">
                    <span className="take-state-lbl" style={{ color: "var(--red)" }}>Failed</span>
                    {take.error && (
                      <span
                        className="take-state-sub"
                        title={take.error}
                        style={{ color: "oklch(0.82 0.12 22)" }}
                      >
                        {take.error}
                      </span>
                    )}
                  </div>
                  <div className="take-time take-time-dim">--:--</div>
                  <div className="take-icons take-icons-dim" />
                </div>
              );
            }

            // Complete — render the waveform row. During the "fade" phase we
            // also render the seg-bar overlay on top, fading out, so the user
            // sees the bar dissolve into the waveform instead of a hard swap.
            const completeRow = (
              <CompleteTakeRow
                jobId={job.jobId}
                takeIndex={take.takeIndex}
                peaks={take.peaks}
                audioUrl={take.audioUrl}
                duration={take.duration}
                durationLabel={take.duration != null ? formatDuration(take.duration) : "—"}
                showLabel={showLabel}
                isThisPlaying={isThisPlaying}
                progress={takeProgress[take.takeIndex] ?? 0}
                shareLoading={shareLoadingFor === take.takeIndex}
                shareError={shareError}
                downloadName={`chatter-${job.jobId}-take${take.takeIndex + 1}.wav`}
                onPlayPause={handlePlayTake}
                onSeek={handleSeek}
                onShare={handleShareTake}
                onTimeUpdate={handleAudioTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={(idx) => {
                  setPlayingTakeIndex((prev) => (prev === idx ? null : prev));
                  setTakeProgress((prev) => ({ ...prev, [idx]: 0 }));
                }}
                audioRef={(el) => {
                  audioRefs.current.set(take.takeIndex, el);
                }}
              />
            );

            if (phase === "fade") {
              return (
                <div key={take.takeIndex} className="take-finishing-stack">
                  {completeRow}
                  <ProcessingTakeRow
                    takeIndex={take.takeIndex}
                    targetPct={100}
                    elapsedFrom={job.startedAt || job.createdAt}
                    onCancel={() => {}}
                    overlay
                  />
                </div>
              );
            }

            return <div key={take.takeIndex}>{completeRow}</div>;
          })}
        </div>
      )}

      {/* Job-level Duplicate (visible once when there are takes to act on) */}
      {(job.status === "complete" || job.status === "processing") && takes.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={(e) => handleRetry(e)}
            title="Duplicate with same settings"
          >
            <Icon.Refresh /> Duplicate
          </button>
        </div>
      )}

      {/* Admin stats */}
      {isComplete && isAdmin && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 16px",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 10.5,
            color: "var(--ink-3)",
            letterSpacing: "0.04em",
          }}
        >
          {job.genTimeSeconds != null && (
            <span>
              total <span style={{ color: "var(--ink-2)" }}>{job.genTimeSeconds.toFixed(1)}s</span>
            </span>
          )}
          {job.inferenceSeconds != null && (
            <span>
              inference <span style={{ color: "var(--ink-2)" }}>{job.inferenceSeconds.toFixed(1)}s</span>
            </span>
          )}
          {job.gpuType && (
            <span>
              gpu <span style={{ color: "var(--ink-2)" }}>{job.gpuType}</span>
            </span>
          )}
          {job.totalJobCost != null && (
            <span>
              cost <span style={{ color: "var(--green)" }}>${job.totalJobCost.toFixed(4)}</span>
            </span>
          )}
        </div>
      )}

      {/* Video sub-jobs */}
      {hasUsableTake && (
        <>
          {videos.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              {videos.map((v) => (
                <VideoJobCard
                  key={v.videoJobId}
                  video={v}
                  parentJobId={job.jobId}
                  onDelete={handleVideoDelete}
                  onReload={handleVideoReload}
                />
              ))}
            </div>
          )}

          {!showVideoPanel ? (
            <button
              type="button"
              className="btn btn-line"
              style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
              onClick={() => setShowVideoPanel(true)}
            >
              <Icon.Image /> Generate video
            </button>
          ) : (
            <div style={{ marginTop: 12 }}>
              <VideoConfigPanel
                job={job}
                initialValues={videoPanelInitial}
                onSubmit={handleVideoSubmit}
                onClose={() => {
                  setShowVideoPanel(false);
                  setVideoPanelInitial(undefined);
                }}
              />
            </div>
          )}
        </>
      )}

      {/* Footer: timestamps + actions */}
      <div className="takes-foot">
        <div
          className="l"
          style={{
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 10.5,
            color: "var(--ink-4)",
            letterSpacing: "0.04em",
            lineHeight: 1.7,
          }}
        >
          <div>started {new Date(job.createdAt).toLocaleString()}</div>
          {job.completedAt && <div>finished {new Date(job.completedAt).toLocaleString()}</div>}
        </div>
        {!selectionMode && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {isActive ? (
              <>
                <button type="button" className="btn btn-ghost" onClick={(e) => handleRetry(e)}>
                  <Icon.Refresh /> Duplicate
                </button>
                <button type="button" className="btn btn-danger" onClick={(e) => handleCancel(e)}>
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-danger"
                onClick={(e) => handleDelete(e)}
                onBlur={() => setConfirmingDelete(false)}
              >
                <Icon.Trash />
                {confirmingDelete ? "Confirm delete?" : "Delete"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showLogModal &&
        createPortal(
          <AdminLogModal job={job} onClose={() => setShowLogModal(false)} />,
          document.body
        )}

      {shareUrl &&
        createPortal(
          <ShareModal shareUrl={shareUrl} onClose={() => setShareUrl(null)} />,
          document.body
        )}
    </div>
  );
}
